import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import jwtDecode from "jwt-decode";
import { enableCrossTabAuthSync } from "@axio/auth";
import { actions, useAppState } from "./AppStateContext";
import {
  MeQuery,
  useGetUserPermissionsQuery,
  useLogoutMutation,
  useMeQuery,
  useValidateSessionQuery,
} from "@axio/graphql/generated/graphql";
import { usePostHog } from "posthog-js/react";
import Sentry from "../utils/sentry";
import { UserRoleUI, getUserRoleFromPermissions } from "@axio/auth-guard";
import { apolloClient } from "../graphqlClient";
import { authClient } from "../auth/client";
import { subscribeAuthEvents } from "../utils/authEvents";
import {
  clearStoredTokens,
  getStoredRefreshToken,
  getValidAccessToken,
  isTokenExpired,
  setLogoutInProgress,
  storeAccessToken,
  storeAuthTokens,
} from "../utils/authRefresh";

interface DecodedToken {
  userId: string;
  email: string;
  name: string;
  deviceId: string;
  isEmailVerified: boolean;
  otpVerifiedForSession: boolean;
  iat: number;
  exp: number;
}

type AuthLoginResult = {
  isEmailVerified: boolean;
  otpVerifiedForSession: boolean;
  userRole: UserRoleUI;
};

type LoginOptions = {
  // When false, login() resolves as soon as the session is established (tokens
  // stored + access token decoded) instead of waiting on the me/permissions
  // network round-trip. The user data still hydrates in the background via the
  // skip-gated queries. LTI launches use this because they only need the
  // decoded token's verification flags to render and gate role checks on
  // isAuthReady. Defaults to true to preserve the password-login redirect,
  // which needs the resolved userRole synchronously.
  awaitUserData?: boolean;
};

type AuthState = {
  isLoading: boolean;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  otpVerifiedForSession: boolean;
  didCheckToken: boolean;
};

type AuthAction = { type: "SET_LOADING"; payload: boolean } | { type: "SET_AUTH_STATE"; payload: Partial<AuthState> };

interface AuthContextType {
  meData: MeQuery | null;
  refetchMe: () => Promise<any>;
  loadingMe: boolean;
  isUpdating: boolean;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  otpVerifiedForSession: boolean;
  login: (accessToken: string, refreshToken: string, options?: LoginOptions) => Promise<AuthLoginResult>;
  logout: () => Promise<void>;
  verifyOTPForSession: (accessToken: string) => Promise<void>;
  userInfo: {
    userId: string;
    userName: string;
    userInitial: string;
    userRole: UserRoleUI;
    userPermissions: string[];
  };
  didCheckToken: boolean;
  isAuthReady: boolean;
  isUserDataReady: boolean;
  isPermissionsReady: boolean;
}

export const decodeToken = async (token: string) => {
  const decoded = jwtDecode<DecodedToken>(token);
  return decoded;
};

export const AuthContext = createContext<AuthContextType>({
  meData: null,
  refetchMe: async () => ({}),
  loadingMe: false,
  isUpdating: false,
  isAuthenticated: false,
  isEmailVerified: false,
  otpVerifiedForSession: false,
  login: async () => ({
    isEmailVerified: false,
    otpVerifiedForSession: false,
    userRole: UserRoleUI.STUDENT,
  }),
  logout: async () => {},
  verifyOTPForSession: async () => {},
  userInfo: {
    userId: "",
    userName: "",
    userInitial: "",
    userRole: UserRoleUI.STUDENT,
    userPermissions: [],
  },
  didCheckToken: false,
  isAuthReady: false,
  isUserDataReady: false,
  isPermissionsReady: false,
});

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_AUTH_STATE":
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const initialAuthState: AuthState = {
  isLoading: true,
  isAuthenticated: false,
  isEmailVerified: false,
  otpVerifiedForSession: false,
  didCheckToken: false,
};

export const AuthProvider = ({ children }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [state, dispatch] = useReducer(authReducer, initialAuthState);
  const explicitLogoutRef = useRef(0);
  const ignoredAuthEventRef = useRef<"tokensUpdated" | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  // login() dedupe: the navigator remounts as auth state settles (Stack.Navigator
  // key changes when isAuthenticated/role change), which re-runs each LTI view's
  // bootstrap login() with the SAME launch token. These refs let repeat calls for
  // an already-established token short-circuit instead of redoing clearStore +
  // me/permissions on every remount.
  const establishedLoginTokenRef = useRef<string | null>(null);
  const inFlightLoginRef = useRef<{ token: string; promise: Promise<AuthLoginResult> } | null>(null);
  const latestUserRoleRef = useRef<UserRoleUI>(UserRoleUI.STUDENT);

  const { dispatch: appDispatch } = useAppState();

  const posthog = usePostHog();

  const clearObservabilityUser = useCallback(() => {
    posthog?.reset(true);
    datadogRum.clearUser();
  }, [posthog]);

  const setObservabilityUserFromToken = useCallback(
    (decodedAccessToken: DecodedToken) => {
      posthog?.identify(decodedAccessToken.userId);
      datadogRum.setUser({
        id: decodedAccessToken.userId,
        name: decodedAccessToken.name,
        email: decodedAccessToken.email,
      });
    },
    [posthog]
  );

  const setObservabilityUserFromMe = useCallback((me: NonNullable<MeQuery["me"]>) => {
    datadogRum.setUser({
      id: me.id,
      name: me.name,
      email: me.email,
      isPayingUser: me.isPro,
      hasFreeTrial: !!me.trialEndDate,
    });
  }, []);

  const getUserIdFromAccessToken = useCallback((accessToken: string | null) => {
    if (!accessToken) {
      return null;
    }

    try {
      return jwtDecode<DecodedToken>(accessToken).userId;
    } catch {
      return null;
    }
  }, []);

  const { data: userPermissionsData, refetch: refetchUserPermissions } = useGetUserPermissionsQuery({
    skip: !state.isAuthenticated,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: meData,
    loading: loadingMe,
    refetch: refetchMe,
  } = useMeQuery({
    skip: !state.isAuthenticated,
    fetchPolicy: "cache-and-network",
  });

  const [hasHydratedUser, setHasHydratedUser] = useState(false);
  const [hasHydratedPermissions, setHasHydratedPermissions] = useState(false);

  useEffect(() => {
    if (meData?.me) {
      setHasHydratedUser(true);
    }
  }, [meData?.me]);

  useEffect(() => {
    if (userPermissionsData?.getUserPermissions) {
      setHasHydratedPermissions(true);
    }
  }, [userPermissionsData?.getUserPermissions]);

  useEffect(() => {
    if (!state.isAuthenticated) {
      setHasHydratedUser(false);
      setHasHydratedPermissions(false);
      // Drop the dedupe marker so a fresh login (e.g. after logout) is not
      // short-circuited as already-established.
      establishedLoginTokenRef.current = null;
    }
  }, [state.isAuthenticated]);

  const userRole = useMemo(
    () => getUserRoleFromPermissions(userPermissionsData?.getUserPermissions),
    [userPermissionsData?.getUserPermissions]
  );

  // Keep a ref of the latest resolved role so the login() short-circuit can return
  // it synchronously without depending on (and thus re-creating) the login callback.
  useEffect(() => {
    latestUserRoleRef.current = userRole;
  }, [userRole]);

  const userInfo = useMemo(() => {
    const userName = meData?.me?.name || "";
    return {
      userId: meData?.me?.id || "",
      userName,
      userInitial: userName ? userName.charAt(0) : "",
      userRole: userRole,
      userPermissions: userPermissionsData?.getUserPermissions || [],
    };
  }, [meData?.me?.id, meData?.me?.name, userRole, userPermissionsData?.getUserPermissions]);

  const { data, startPolling, stopPolling } = useValidateSessionQuery({
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "network-only",
    skip: !state.isAuthenticated,
  });

  const [logoutMutation] = useLogoutMutation();

  const hydrateAuthState = useCallback(
    async (accessToken: string | null, refreshToken: string | null): Promise<DecodedToken | null> => {
      const hasValidAccessToken = accessToken && !isTokenExpired(accessToken, 30);
      let resolvedAccessToken = hasValidAccessToken ? accessToken : null;

      if (!resolvedAccessToken && refreshToken) {
        resolvedAccessToken = await getValidAccessToken({ forceRefresh: true });
      }

      if (resolvedAccessToken && refreshToken) {
        try {
          const decodedAccessToken = await decodeToken(resolvedAccessToken);
          currentUserIdRef.current = decodedAccessToken.userId;
          setObservabilityUserFromToken(decodedAccessToken);
          dispatch({
            type: "SET_AUTH_STATE",
            payload: {
              isAuthenticated: true,
              isEmailVerified: decodedAccessToken.isEmailVerified,
              otpVerifiedForSession: decodedAccessToken.otpVerifiedForSession,
              didCheckToken: true,
            },
          });
          return decodedAccessToken;
        } catch (error) {
          currentUserIdRef.current = null;
          Sentry.captureException(error);
          await clearStoredTokens("token_decode_failed");
          clearObservabilityUser();
        }
      } else {
        currentUserIdRef.current = null;
        clearObservabilityUser();
      }

      dispatch({
        type: "SET_AUTH_STATE",
        payload: {
          isAuthenticated: false,
          isEmailVerified: false,
          otpVerifiedForSession: false,
          didCheckToken: true,
        },
      });

      return null;
    },
    [clearObservabilityUser, setObservabilityUserFromToken]
  );

  const initializeAuth = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    if (typeof window !== "undefined") {
      const path = window.location?.pathname ?? "";
      const isAuthCallbackPath = path === "/login/lti" || path === "/login/sso" || path === "/login/lti-ile";
      if (isAuthCallbackPath) {
        const params = new URLSearchParams(window.location.search);
        const hasCallbackTokens = params.has("accessToken") && params.has("refreshToken");
        if (hasCallbackTokens) {
          dispatch({
            type: "SET_AUTH_STATE",
            payload: {
              isAuthenticated: false,
              isEmailVerified: false,
              otpVerifiedForSession: false,
              didCheckToken: true,
            },
          });
          dispatch({ type: "SET_LOADING", payload: false });
          return;
        }
      }
    }
    const [accessToken, refreshToken] = await Promise.all([
      getValidAccessToken({ minTtlSeconds: 30 }),
      getStoredRefreshToken(),
    ]);
    await hydrateAuthState(accessToken, refreshToken);
    dispatch({ type: "SET_LOADING", payload: false });
  }, [hydrateAuthState]);

  const handleTokensUpdated = useCallback(
    async (accessToken: string, refreshToken: string) => {
      const nextUserId = getUserIdFromAccessToken(accessToken);
      const isIdentityChange = state.isAuthenticated && currentUserIdRef.current !== nextUserId;

      if (!state.isAuthenticated || isIdentityChange) {
        setHasHydratedUser(false);
        setHasHydratedPermissions(false);
      }

      const decodedAccessToken = await hydrateAuthState(accessToken, refreshToken);

      if (!decodedAccessToken || !isIdentityChange) {
        return;
      }

      try {
        if (apolloClient) {
          await apolloClient.clearStore();
        }

        appDispatch({
          type: actions.RESET_STATE,
        });

        const meResult = await refetchMe();
        await refetchUserPermissions();

        const me = meResult?.data?.me;
        if (me) {
          setObservabilityUserFromMe(me);
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    [
      appDispatch,
      getUserIdFromAccessToken,
      hydrateAuthState,
      refetchMe,
      refetchUserPermissions,
      setObservabilityUserFromMe,
      state.isAuthenticated,
    ]
  );

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  useEffect(() => {
    const checkSession = async () => {
      if (data && !data.validateSession.isValid) {
        clearObservabilityUser();
      }
    };

    checkSession();
  }, [clearObservabilityUser, data]);

  useEffect(() => {
    if (!state.isAuthenticated) {
      stopPolling();
      return;
    }

    const pollInterval = 60000;
    startPolling(pollInterval);

    return () => {
      stopPolling();
    };
  }, [startPolling, state.isAuthenticated, stopPolling]);

  const login = useCallback(
    async (accessToken: string, refreshToken: string, options?: LoginOptions): Promise<AuthLoginResult> => {
      const awaitUserData = options?.awaitUserData ?? true;

      // Build the result from the (cheap, synchronous) token decode plus the latest
      // resolved role. Used by the dedupe short-circuit below.
      const resultFromToken = (): AuthLoginResult => {
        let decoded: DecodedToken | null = null;
        try {
          decoded = jwtDecode<DecodedToken>(accessToken);
        } catch {
          decoded = null;
        }
        return {
          isEmailVerified: decoded?.isEmailVerified ?? false,
          otpVerifiedForSession: decoded?.otpVerifiedForSession ?? false,
          userRole: latestUserRoleRef.current,
        };
      };

      // Dedupe 1: a login for this exact token is already running (concurrent
      // remounts) — reuse it instead of starting a second clearStore + refetch.
      if (inFlightLoginRef.current && inFlightLoginRef.current.token === accessToken) {
        return inFlightLoginRef.current.promise;
      }

      // Dedupe 2: the session for this exact launch token is already established
      // (the navigator remounted and the view re-ran bootstrap with the same token).
      // Skip the expensive clearStore + me/permissions and return current state.
      if (establishedLoginTokenRef.current === accessToken) {
        return resultFromToken();
      }

      const run = async (): Promise<AuthLoginResult> => {
        setIsUpdating(true);
        let decodedAccessToken: DecodedToken | null = null;
        let userRoleFromPermissions = UserRoleUI.STUDENT;

        // Fetch me/permissions, push them to analytics/observability, and resolve the
        // user's role. Hydrating these into the cache is what flips hasHydratedUser/
        // hasHydratedPermissions (and therefore isAuthReady) for the rest of the app.
        const hydrateUserData = async (): Promise<UserRoleUI> => {
          const [meResult, permissionsResult] = await Promise.all([refetchMe(), refetchUserPermissions()]);
          const me = meResult?.data?.me;

          if (me) {
            posthog?.capture("Set user properties on login.", {
              $set: {
                isPayingUser: me.isPro,
                hasFreeTrial: !!me.trialEndDate,
              },
            });
            setObservabilityUserFromMe(me);
          }

          return getUserRoleFromPermissions(permissionsResult?.data?.getUserPermissions);
        };

        try {
          ignoredAuthEventRef.current = "tokensUpdated";
          await storeAuthTokens(accessToken, refreshToken);

          if (apolloClient) {
            await apolloClient.clearStore();
          }

          appDispatch({
            type: actions.RESET_STATE,
          });

          decodedAccessToken = await hydrateAuthState(accessToken, refreshToken);

          // Mark the session established so remount-driven repeat calls for this
          // same token short-circuit (see Dedupe 2 above).
          establishedLoginTokenRef.current = accessToken;

          if (awaitUserData) {
            userRoleFromPermissions = await hydrateUserData();
          } else {
            // Don't block the caller on the user-data round-trip. me/permissions
            // hydrate in the background; callers that need the role gate on
            // isAuthReady / userInfo.userRole from context instead of this return.
            void hydrateUserData().catch((error) => Sentry.captureException(error));
          }
        } catch (error) {
          Sentry.captureException(error);
        } finally {
          if (ignoredAuthEventRef.current === "tokensUpdated") {
            ignoredAuthEventRef.current = null;
          }
          setIsUpdating(false);
        }

        return {
          isEmailVerified: decodedAccessToken?.isEmailVerified ?? false,
          otpVerifiedForSession: decodedAccessToken?.otpVerifiedForSession ?? false,
          userRole: userRoleFromPermissions,
        };
      };

      const promise = run();
      inFlightLoginRef.current = { token: accessToken, promise };
      try {
        return await promise;
      } finally {
        if (inFlightLoginRef.current?.token === accessToken) {
          inFlightLoginRef.current = null;
        }
      }
    },
    [appDispatch, hydrateAuthState, posthog, refetchMe, refetchUserPermissions, setObservabilityUserFromMe]
  );

  const logout = useCallback(async () => {
    explicitLogoutRef.current += 1;
    setLogoutInProgress(true);
    setIsUpdating(true);

    dispatch({
      type: "SET_AUTH_STATE",
      payload: {
        isAuthenticated: false,
        isEmailVerified: false,
        otpVerifiedForSession: false,
        didCheckToken: true,
      },
    });

    await clearStoredTokens("user_logout");
    currentUserIdRef.current = null;
    clearObservabilityUser();

    try {
      const result = await logoutMutation();

      if (!result?.data?.logout?.success) {
        throw new Error("Failed to logout");
      }

      if (apolloClient) {
        await apolloClient.clearStore();
      }

      appDispatch({
        type: actions.RESET_STATE,
      });
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsUpdating(false);
      setLogoutInProgress(false);
      explicitLogoutRef.current = Math.max(0, explicitLogoutRef.current - 1);
    }
  }, [appDispatch, clearObservabilityUser, logoutMutation]);

  const forceLogout = useCallback(
    async (_reason?: string) => {
      setIsUpdating(true);

      dispatch({
        type: "SET_AUTH_STATE",
        payload: {
          isAuthenticated: false,
          isEmailVerified: false,
          otpVerifiedForSession: false,
          didCheckToken: true,
        },
      });

      await clearStoredTokens("force_logout");
      currentUserIdRef.current = null;
      clearObservabilityUser();

      try {
        if (apolloClient) {
          await apolloClient.clearStore();
        }

        appDispatch({
          type: actions.RESET_STATE,
        });
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [appDispatch, clearObservabilityUser]
  );

  useEffect(() => {
    const unsubscribe = subscribeAuthEvents((event) => {
      if (event.type === "logout") {
        if (explicitLogoutRef.current > 0) {
          return;
        }
        forceLogout(event.reason);
        return;
      }

      if (event.type === "tokensUpdated") {
        if (ignoredAuthEventRef.current === "tokensUpdated") {
          ignoredAuthEventRef.current = null;
          return;
        }

        void handleTokensUpdated(event.accessToken, event.refreshToken);
      }
    });

    return unsubscribe;
  }, [forceLogout, handleTokensUpdated]);

  useEffect(() => {
    return enableCrossTabAuthSync(authClient);
  }, []);

  const verifyOTPForSession = useCallback(
    async (accessToken: string) => {
      setIsUpdating(true);
      try {
        const storedRefreshToken = await getStoredRefreshToken();
        if (storedRefreshToken) {
          ignoredAuthEventRef.current = "tokensUpdated";
          await storeAuthTokens(accessToken, storedRefreshToken);
        } else {
          await storeAccessToken(accessToken);
        }
        const refreshedToken = await getStoredRefreshToken();
        await hydrateAuthState(accessToken, refreshedToken);
        await refetchMe();
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        if (ignoredAuthEventRef.current === "tokensUpdated") {
          ignoredAuthEventRef.current = null;
        }
        setIsUpdating(false);
      }
    },
    [hydrateAuthState, refetchMe]
  );

  const isUserDataReady = !state.isAuthenticated || hasHydratedUser;
  const isPermissionsReady = !state.isAuthenticated || hasHydratedPermissions;
  const isAuthReady =
    !state.isLoading && state.didCheckToken && (!state.isAuthenticated || (hasHydratedUser && hasHydratedPermissions));

  const value = useMemo(() => {
    return {
      meData: meData,
      refetchMe,
      loadingMe,
      userInfo,
      isUpdating,
      isAuthenticated: state.isAuthenticated,
      isEmailVerified: state.isEmailVerified,
      otpVerifiedForSession: state.otpVerifiedForSession,
      login,
      logout,
      verifyOTPForSession,
      didCheckToken: state.didCheckToken,
      isAuthReady,
      isUserDataReady,
      isPermissionsReady,
    };
  }, [
    isUpdating,
    isAuthReady,
    isPermissionsReady,
    isUserDataReady,
    loadingMe,
    login,
    logout,
    meData,
    refetchMe,
    state.didCheckToken,
    state.isAuthenticated,
    state.isEmailVerified,
    state.otpVerifiedForSession,
    userInfo,
    verifyOTPForSession,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
